import * as Yup from 'yup';
import type { Companion } from './uploader-connection';

// --- Vendor instructions (matches Go VendorInstructions + FormatInstructions) ---

export const FormatInstructionsSchema = Yup.object().shape({
  format: Yup.string().required(),
  label: Yup.string().required(),
  content: Yup.string().required(),
});

export type FormatInstructions = Yup.InferType<typeof FormatInstructionsSchema>;

export const VendorInstructionsSchema = Yup.object().shape({
  vendor_normalized_name: Yup.string().required(),
  // `_default/_vendor.md` ships `display_name: ""`, and any unrecognized vendor
  // key falls back to `_default`, so "" is a real response value here.
  display_name: Yup.string().defined(),
  portal_url: Yup.string().defined(),
  formats: Yup.array().of(FormatInstructionsSchema.required()).required(),
});

export type VendorInstructions = Yup.InferType<typeof VendorInstructionsSchema>;

// --- Processing result (matches receive-invoices-sync JSON response) ---

export const ReceivingFileSchema = Yup.object().shape({
  name: Yup.string().required(),
  content: Yup.string().required(),
  content_hash: Yup.string().required(),
  sources: Yup.array().of(Yup.string().required()).required(),
});

export type ReceivingFile = Yup.InferType<typeof ReceivingFileSchema>;

export const ProductOrderSchema = Yup.object().shape({
  raw_item_code: Yup.string().defined(),
  item_code: Yup.string().defined(),
  suggested_item_code: Yup.string().defined(),
  barcode: Yup.string().defined(),
  brand: Yup.string().defined(),
  description: Yup.string().defined(),
  size: Yup.string().defined(),
  case_qty: Yup.number().defined(),
  cases_ordered: Yup.number().required(),
  units_ordered: Yup.number().required(),
  sales_price: Yup.number().required(),
});

export type ProductOrder = Yup.InferType<typeof ProductOrderSchema>;

export const UNKNOWN_REASON_DUPLICATE_ITEM_CODE = 'duplicate_item_code';

export const ReceivingConflictSchema = Yup.object().shape({
  product_id: Yup.string().defined(),
  brand: Yup.string().defined(),
  description: Yup.string().defined(),
  size: Yup.string().defined(),
});

export type ReceivingConflict = Yup.InferType<typeof ReceivingConflictSchema>;

// Unknown rows extend a parsed order with why they didn't match. Both fields
// use .default() because processing results are persisted on the session and
// rehydrated from cache raw, bypassing Yup casting — a bare .defined() would
// throw on sessions created before these fields existed. `conflicts` is
// omitempty on the Go side (absent for no_match), so undefined → [].
export const UnknownProductSchema = ProductOrderSchema.shape({
  unknown_reason: Yup.string().default(''),
  conflicts: Yup.array().of(ReceivingConflictSchema.required()).default([]),
});

export type UnknownProduct = Yup.InferType<typeof UnknownProductSchema>;

// Splits unknown rows into item-code collisions vs. everything else. `noMatch`
// is the negation so rows from older sessions (empty reason) fall through to it.
export function partitionUnknownProducts(unknowns: UnknownProduct[]) {
  return {
    duplicateItemCodes: unknowns.filter(
      (u) => u.unknown_reason === UNKNOWN_REASON_DUPLICATE_ITEM_CODE
    ),
    noMatch: unknowns.filter((u) => u.unknown_reason !== UNKNOWN_REASON_DUPLICATE_ITEM_CODE),
  };
}

export const ProductOrderMatchSchema = Yup.object().shape({
  product: ProductOrderSchema.required(),
  matched_by: Yup.string().required(),
});

export type ProductOrderMatch = Yup.InferType<typeof ProductOrderMatchSchema>;

export const UnparsedRowSchema = Yup.object().shape({
  filename: Yup.string().required(),
  row: Yup.number().required(),
  // Raw CSV cells: blank cells are "" (not missing data), so the element
  // validator must accept "". The slice itself is always non-nil
  // (`append([]string(nil), …)`). This was the Mary's Liquor drop bug.
  data: Yup.array().of(Yup.string().defined()).required(),
  err: Yup.string().defined(),
});

export type UnparsedRow = Yup.InferType<typeof UnparsedRowSchema>;

// When Go serializes the processing result, every field is always present —
// nil slices/maps/pointers become JSON `null`. So `.nullable().defined()` is
// correct for each field here. The outer `processing_result` on the session
// is handled with `Yup.lazy` so an absent result doesn't cascade-cast into
// an empty object and blow up these inner `.defined()` checks.
export const ProcessingResultSchema = Yup.object().shape({
  receiving_file: ReceivingFileSchema.nullable().defined(),
  parse_errors: Yup.object()
    .nullable()
    .test(
      'string-values',
      'parse_errors must be a {string: string} map',
      (val) => val == null || Object.values(val).every((v) => typeof v === 'string')
    )
    .defined(),
  matched: Yup.array().of(ProductOrderMatchSchema.required()).nullable().defined(),
  not_parsed_rows: Yup.array().of(UnparsedRowSchema.required()).nullable().defined(),
  unknown_products: Yup.array().of(UnknownProductSchema.required()).nullable().defined(),
});

export type ProcessingResult = Yup.InferType<typeof ProcessingResultSchema>;

// --- Session state (matches Go models.ReceivingSessionState, post-MR-1a) ---

export const receivingSessionStates = [
  'selecting_companion', // NEW — first state in the flow
  'selecting_vendor',
  'get_invoices',
  'processing',
  'review',
  'sending_to_lpos',
  'paused_lpos_busy', // net-new: write blocked or LPOS running own receiving
  'waiting_for_lpos',
  'complete',
  'failed',
  'cancelled', // frontend-only sentinel from Phase A (ReceivingStateCancelled)
] as const;

export type ReceivingSessionState = (typeof receivingSessionStates)[number];

// Pre-Confirm states — those that precede POST /confirm (the point of no clean
// return, Invariant 5). The single TS source of truth for Cancel reachability:
// canCancelSession below and the pause screen both derive from it. Keep in sync
// with the Go mirror models.PreConfirmReceivingStates
// (lambdas/api/models/receiving_session.go).
export const PRE_CONFIRM_STATES: ReadonlySet<string> = new Set<ReceivingSessionState>([
  'selecting_companion',
  'selecting_vendor',
  'get_invoices',
  'processing',
  'review',
]);

// States from which the DELETE /cancel endpoint (clean rewind) is allowed.
// Mirrors the backend CancelReachable guard — keep in sync. A `failed` session
// is NOT cancelable: Cancel refuses terminal records (they age out via TTL), and
// the resolution path for a failure is Retry/Abandon from the session page.
// For paused_lpos_busy, Cancel is valid only when paused_from is pre-Confirm (the
// write hadn't started); post-Confirm pauses offer Abandon, not Cancel.
export const canCancelSession = (s: ReceivingSessionState, pausedFrom?: string): boolean => {
  if (PRE_CONFIRM_STATES.has(s)) {
    return true;
  }

  if (s === 'paused_lpos_busy') {
    return pausedFrom != null && PRE_CONFIRM_STATES.has(pausedFrom);
  }

  return false;
};

// States from which Abandon (POST /abandon) is reachable (spec §7, §10).
export const canAbandonSession = (s: ReceivingSessionState): boolean =>
  s === 'failed' || s === 'paused_lpos_busy' || s === 'waiting_for_lpos';

// States in which the given field is populated on the wire. Used by the
// session schema to turn requirements on/off as the state machine progresses.
// `failed` and `cancelled` can occur from any prior state, so their field
// shape depends on what was set before — we don't enforce those strictly.
const STATES_WITH_VENDOR: ReceivingSessionState[] = [
  'get_invoices',
  'processing',
  'review',
  'sending_to_lpos',
  'waiting_for_lpos',
  'complete',
];

const STATES_WITH_PROCESSING_RESULT: ReceivingSessionState[] = [
  'review',
  'sending_to_lpos',
  'waiting_for_lpos',
  'complete',
];

export const receivingSessionFileSources = ['web_upload', 'companion_detected'] as const;

export type ReceivingSessionFileSource = (typeof receivingSessionFileSources)[number];

// Mirrors Go models.ReceivingSessionFile.
export const ReceivingSessionFileSchema = Yup.object().shape({
  filename: Yup.string().required(),
  source: Yup.string<ReceivingSessionFileSource>()
    .oneOf([...receivingSessionFileSources])
    .required(),
  s3_key: Yup.string().required(),
  size: Yup.number().required(),
});

export type ReceivingSessionFile = Yup.InferType<typeof ReceivingSessionFileSchema>;

// Mirrors Go models.ReceivingSession. Intentionally NO `created_at` — the Go
// struct only carries `updated_at` and `ttl`.
//
// Which fields are present depends on the session state; fields become
// required as the state machine progresses. `processing_result` uses
// `Yup.lazy` so an absent value isn't cast to `{}` and cascaded into the
// inner required-field checks.
export const ReceivingSessionSchema = Yup.object().shape({
  store_id: Yup.string().required(),
  session_id: Yup.string().required(),
  state: Yup.string<ReceivingSessionState>()
    .oneOf([...receivingSessionStates])
    .required(),
  machine_id: Yup.string().when('state', ([state], schema) =>
    state === 'selecting_companion' || state === 'cancelled' || state === 'failed'
      ? schema.optional()
      : schema.required()
  ),
  vendor_id: Yup.string().when('state', ([state], schema) =>
    STATES_WITH_VENDOR.includes(state as ReceivingSessionState)
      ? schema.required()
      : schema.optional()
  ),
  files: Yup.array().of(ReceivingSessionFileSchema.required()).optional(),
  processing_result: Yup.lazy((value, options) => {
    const state = (options.parent as { state?: ReceivingSessionState } | undefined)?.state;
    if (value == null) {
      // Absent or null is allowed unless state says it must be present.
      // Returning a non-object schema here avoids Yup's ObjectSchema casting
      // `undefined` → `{}`, which would otherwise fail the inner
      // `.defined()` checks on ProcessingResultSchema.
      const mustBePresent = state && STATES_WITH_PROCESSING_RESULT.includes(state);

      return mustBePresent
        ? Yup.mixed().required('processing_result is required in this state')
        : Yup.mixed().nullable();
    }

    return ProcessingResultSchema;
  }),
  error: Yup.string().when('state', ([state], schema) =>
    state === 'failed' ? schema.required() : schema.optional()
  ),
  failed_from: Yup.string().when('state', ([state], schema) =>
    state === 'failed' ? schema.required() : schema.optional()
  ),

  // Net-new fields (Rev 3 / B16): timestamps + pause cause + content hash.
  // All optional on the wire — absent when state hasn't reached that point yet.
  paused_from: Yup.string().optional(),
  paused_since: Yup.string().optional(),
  waiting_since: Yup.string().optional(),
  content_hash: Yup.string().optional(),

  active_viewer_tab_id: Yup.string().optional(),
  updated_at: Yup.string().required(),
  ttl: Yup.number().required(),
});

export type ReceivingSession = Yup.InferType<typeof ReceivingSessionSchema>;

// A companion can't take on a new or resumed session while it already has an
// active session assigned, or while its disk still holds a pending LPOS import
// file (rcv_blocked). Single source of truth for "is this companion free?",
// used by both the receiving list page (enabling "Start new receiving" /
// Resume) and the in-session companion picker — keep the two in sync.
export function makeCompanionBusyPredicate(
  activeSessions: ReceivingSession[]
): (companion: Companion) => boolean {
  const machineIdsWithSession = new Set(
    activeSessions.map((s) => s.machine_id).filter((id): id is string => id != null && id !== '')
  );

  return (companion) =>
    machineIdsWithSession.has(companion.uploader.machine_id) ||
    companion.uploader.rcv_blocked === true;
}
